import type { AgentUseCase, UseCaseContent } from "./types";

const en: UseCaseContent = {
  industry: "HR, IT & internal ops",
  title: "Internal knowledge agent",
  cardSummary:
    "Staff ping HR, IT and ops all day with questions already answered in your own documents.",
  heroSubhead:
    "We design, build and deploy an AI agent that answers your team's questions from your own SOPs, policies, HR handbook and product docs — with the exact source cited, and an honest “that's not in our documents” when it isn't.",
  snapshot: {
    valueLabel: "High",
    valueWhy:
      "Frees your experts, HR and IT from re-answering the same questions all day, and gives every employee an instant, consistent, sourced answer — value spread across the whole company.",
    complexityLabel: "Medium",
    complexityWhy:
      "Retrieval over your documents, keeping the index fresh, grounding every answer in a citation, a calibrated “I don't know”, and role-based access so people only see what they're allowed to.",
    timeToLive: "~a few weeks (one document set)",
    bestFit: "Companies where staff repeatedly ask HR/IT/ops questions already in the docs",
  },
  outcomes: [
    "Experts, HR and IT stop answering the same questions all day",
    "Every employee gets an instant, consistent answer — with the source",
    "New hires get up to speed without shoulder-tapping colleagues",
    "No made-up answers: it cites the document, or says it doesn't know",
  ],
  offTheShelf: {
    title: "Off-the-shelf first: you may not need a custom build",
    body: "Be honest before you build. If your documents already live in tools Claude connects to — Google Drive, SharePoint, Slack, GitHub and more — and you're OK with per-seat pricing, Claude for Work with connectors can stand up an internal knowledge agent with almost no engineering: point it at your sources and go. As a Claude partner we'll say it plainly — if that's all you need, start with a plan. A custom build (the rest of this page) earns its keep only when you need what the seat product can't do on its own: role-based access your compliance team can prove, retrieval scoped per department, PDPA-bound or on-prem data that can't sit in a seat product, your own chat surface like WhatsApp or Teams, or auditable citations and an eval gate.",
    plansCta: "Compare Claude plans",
    costCta: "Estimate the seat cost",
  },
  impact: {
    manualLabel: "Finding the answer today",
    manualValue: "~10–20 min",
    agentLabel: "With the agent",
    agentValue: "~seconds",
    saved:
      "≈ 10–15 minutes per question — plus the hidden interrupt cost on your experts. At 500 questions a month that's ~80–125 hours of staff time back.",
    note: "Indicative; actual savings depend on how often staff currently hunt for answers or interrupt colleagues.",
  },
  businessProblem:
    "In most companies in the region the answer to “what's our medical claim limit?”, “how do I apply for leave?”, “which form do I use for a supplier?” already exists — in an HR handbook, an SOP, a policy PDF, a product manual. But nobody can find it. So staff message HR, IT or a senior colleague, wait for a reply, and the same questions get answered dozens of times a week. The knowledge is there; the access isn't.",
  manualWorkflow: {
    steps: [
      "Employee has a question",
      "Searches the shared drive / intranet, gives up",
      "Messages HR / IT / a senior colleague",
      "Waits for a reply (minutes to hours)",
      "The expert stops their own work to answer — again",
    ],
    breaks:
      "The bottleneck isn't missing knowledge — it's retrieval. The answer exists in a document nobody can find, so it gets re-asked and re-answered.",
  },
  agenticWorkflow: {
    narrative:
      "We turn your documents into an agent staff can just ask. It works out which knowledge source the question belongs to, retrieves the relevant passages, drafts a grounded answer, and verifies every claim traces to a citation before replying — and abstains honestly when the answer isn't in the docs.",
    steps: [
      "Understand the question",
      "Retrieve the relevant passages",
      "Draft a grounded answer",
      "Verify every claim cites a source",
      "Reply with the answer + source (or say it doesn't know)",
    ],
    inLabel: "A staff question arrives",
    outLabel: "A sourced answer (or an honest “not in the docs”)",
    loopAround: {
      index: 3,
      label: "If a claim isn't backed by a retrieved source, it re-retrieves or abstains rather than guess.",
    },
    humanStep: "Unanswerable or sensitive questions route to the right person",
  },
  workedExample: {
    inIcon: "whatsapp",
    inLabel: "Staff message",
    inMessage:
      "eh nak tanya, panel clinic claim limit berapa ah? and boleh claim non-panel tak?",
    outLabel: "Sourced answer",
    outLines: [
      "Panel clinic: RM 500 per visit, outpatient (HR Handbook §4.2)",
      "Non-panel: not covered unless pre-approved by your manager (§4.3)",
      "Claim via the HR portal within 30 days, keep the receipt",
    ],
    outResult: "Answered in seconds, every line cited — no message to HR",
    note: "Cites the exact document & section · says “not in our docs” rather than guess · ~RM 0.01–0.05 per question.",
  },
  workflowDetail: [
    {
      title: "Understand the question",
      detail:
        "It interprets the question — including mixed Bahasa Malaysia / English / Chinese and vague phrasing — and works out which knowledge domain it belongs to: HR, IT, finance policy, or a product manual.",
    },
    {
      title: "Retrieve the right passages",
      detail:
        "It searches the indexed document set (semantic plus keyword) and pulls the specific passages that answer the question — not the whole document — so the answer is grounded in exact text.",
    },
    {
      title: "Draft a grounded answer",
      detail:
        "It writes a short, direct answer using only the retrieved passages, in the language the person asked in, and keeps the source reference attached to each point.",
    },
    {
      title: "Verify the citations (the check)",
      detail:
        "Every claim in the answer must map to a retrieved source; if a sentence isn't backed, it re-retrieves or drops it — so it cannot confidently state something that isn't in your documents.",
    },
    {
      title: "Answer or abstain honestly",
      detail:
        "It returns the answer with the source, or says “I couldn't find that in our documents” and offers to route the question on. No confident guesses.",
    },
    {
      title: "Route the exceptions to a human",
      detail:
        "Sensitive, out-of-scope or genuinely unanswerable questions go to the right owner — HR or IT — with the question attached, never a dead end.",
    },
  ],
  architecture: [
    "Pattern: a retrieve → ground → cite-check chain on the Claude Agent SDK, with a light routing front-end that picks the right document set per question — each capability exposed as an MCP tool.",
    "Ingestion & retrieval (RAG): documents are chunked by their own structure — sections, clauses, tables — so each passage is self-contained and keeps its citation, not blind fixed-size slices that cut a policy mid-clause. Retrieval is hybrid: a vector (embeddings) index for meaning plus a keyword index for terms that must match exactly — policy codes, form numbers, “§4.2”, SKUs; we over-retrieve then re-rank the top passages before the model answers. Pure semantic search misses exact identifiers; pure keyword misses paraphrase — hybrid catches both.",
    "Context & freshness: only the re-ranked passages enter the window (prompt-cached for the stable system context), and the index is rebuilt incrementally on document change — so an updated SOP is live the moment it's published, no stale answers.",
    "Tools: the search/retrieve tools and a topic router/classifier — all read-only; no tool can write to your source systems.",
    "Grounding & citations: answers are constrained to the retrieved passages and every claim carries a source; unsupported sentences are dropped — the anti-hallucination guarantee, enforced, not hoped for.",
    "Access control: retrieval is scoped to the asker's role and enforced at the tool layer — restricted HR or finance documents are only searchable by people entitled to them, so the agent can't surface what someone isn't cleared to see.",
    "Untrusted input: both the question and the document text are treated as untrusted (prompt-injection resistant) — a booby-trapped document can't make the agent leak or misbehave.",
    "Observability: every answer logs the question, the passages used and the sources cited — so any answer can be audited, and a wrong or outdated document traced and fixed.",
    "Eval harness: a golden set of real staff questions with known correct answers plus known “not in the docs” cases; scored on both answer correctness and on abstaining when it should — regression-tested on every change and every document update.",
  ],
  models: [
    {
      task: "Classify & route the question",
      model: "Claude Haiku",
      why: "Cheap, fast intent and topic classification at high volume — most questions never need more.",
    },
    {
      task: "Draft the grounded answer",
      model: "Claude Sonnet",
      why: "Strong reasoning over retrieved passages and messy mixed-language questions — the workhorse.",
    },
    {
      task: "Nuanced / high-stakes policy interpretation",
      model: "Claude Opus",
      why: "Reserved for the small share of ambiguous, consequential questions where wording really matters.",
    },
  ],
  cost: {
    rows: [
      {
        label: "AI usage — per question",
        value: "~RM 0.01–0.05",
        note: "short retrieval + grounded answer, mostly at Claude Haiku/Sonnet token rates",
      },
      {
        label: "AI usage — at 500 questions/month",
        value: "~RM 20–100 / month",
        note: "most questions resolve on Haiku; only nuanced ones reach Sonnet or Opus",
      },
      {
        label: "Build (one-off)",
        value: "scoped per document set",
        note: "indexing your documents + wiring the chat surface is a few weeks; we quote fixed after a short discovery",
      },
      {
        label: "Ongoing",
        value: "monitoring + re-indexing",
        note: "small next to the expert time it frees",
      },
    ],
    disclaimer:
      "Indicative only, shown in Ringgit at roughly RM 4.09 to the USD (mid-market, 11 Aug 2026); per-token rates follow Anthropic's published pricing (confirm current figures). Actual AI usage depends on question volume and how much text each answer needs. You can model the same figures in SGD or your currency with our Claude cost calculator.",
  },
  howWeBuild:
    "It answers only from your own documents, never the open internet — so it can't invent a policy you don't have. Every answer carries its source, restricted documents stay restricted to the right roles, and re-indexing on change means an updated SOP is live the moment you publish it, not weeks later.",
  agentLoop: [
    "the question + your indexed documents",
    "search & retrieve the right passages (MCP tools)",
    "check every claim cites a source before replying",
  ],
  integrations: [
    "Your document store — SharePoint, Google Drive, Confluence, Notion or PDFs",
    "The chat surface staff already use — Teams, Slack, WhatsApp or web",
    "Optional: your HRIS / ITSM to route the questions it can't answer",
  ],
  modelChoice: {
    approach: "Single model + a cheap router",
    rationale:
      "Answering from documents is one tightly-scoped task per question, so a single grounded model with a cheap classifier in front is the right call — reliable, cheap, and easy to audit.",
    costNote:
      "Multi-agent designs (≈15× the tokens) buy nothing here — this isn't a parallel, breadth-first task.",
  },
  evaluation: [
    "Grounded-answer checks — every claim in the answer must trace to a retrieved source",
    "Abstention accuracy — it says “not in the docs” when the answer genuinely isn't there",
    "Business KPIs — questions self-served, expert interruptions removed, time-to-answer",
  ],
  evaluationDetail: {
    framework:
      "We score the agent with the Agent GPA framework — Goal, Plan, Action, a current standard for agent reliability — on top of the human baseline.",
    baseline:
      "First we sample how staff get answers today — how long it takes, how often an expert is interrupted, and how consistent the answers are. That human baseline is what every agent metric is measured against, so the lift is provable rather than assumed.",
    dimensions: [
      {
        dim: "Goal",
        desc: "Did it give the right answer?",
        metrics: [
          {
            name: "Answer correctness",
            measures: "On held-out real questions, the answer matches the correct documented answer",
            target: "≥ 95%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "Did it approach the question soundly?",
        metrics: [
          {
            name: "Right source retrieved",
            measures: "It pulls the passage that actually answers the question",
            target: "≥ 95%",
          },
          {
            name: "Abstains when it should",
            measures: "On questions the documents don't cover, it says so instead of guessing",
            target: "≥ 98%",
          },
        ],
      },
      {
        dim: "Action",
        desc: "Were the individual steps correct?",
        metrics: [
          {
            name: "Every claim cited",
            measures: "No sentence in the answer lacks a source",
            target: "100%",
          },
          {
            name: "No hallucinated policy",
            measures: "It never states a rule that isn't in the documents",
            target: "0 tolerated",
          },
          {
            name: "Access respected",
            measures: "It never surfaces a document the asker isn't entitled to see",
            target: "0 tolerated",
          },
        ],
      },
    ],
    gate: "The agent doesn't go live to staff until it beats the human baseline and clears these GPA targets on a golden set of real questions — including ones deliberately not in the docs. Every change and every document update is regression-tested against the same set before it ships.",
  },
  delivery:
    "We start with a proof-of-concept on one document set — your HR handbook or your IT SOPs — and prove it answers correctly and abstains honestly before widening to more sources. A focused first rollout is a matter of weeks.",
  ctaTitle: "Is your team asking HR and IT the same questions all day?",
  ctaDescription:
    "Tell us which documents hold the answers and how your staff ask today — we'll tell you honestly whether an internal knowledge agent is worth building, and what it would take.",
  faqs: [
    {
      question: "Where does the agent get its answers?",
      answer:
        "Only from the documents you give it — your SOPs, policies, handbooks and manuals — never the open internet. If an answer isn't in your documents, it says so rather than guess, and every answer cites the exact source.",
    },
    {
      question: "How does it actually find the right answer in our documents?",
      answer:
        "Two steps. When we set it up, we break your documents into meaningful pieces — by section and clause — so it can quote the exact paragraph rather than point vaguely at a 40-page PDF. Then, for each question, it searches two ways at once: by meaning (so “how much can I claim?” finds the “RM 500 limit” clause even in different words) and by exact term (so “form BE” or “clause 4.2” matches literally). It takes the best-matching passages, answers only from those, and shows the source. Searching by meaning alone would miss exact codes; by keyword alone would miss the paraphrased questions people actually ask — doing both is why it lands on the right passage.",
    },
    {
      question: "Can staff see documents they're not allowed to?",
      answer:
        "No. Retrieval is scoped to each person's role and enforced at the tool layer — restricted HR or finance documents are only searchable by people entitled to them. The agent can't surface what someone isn't cleared to see.",
    },
    {
      question: "What happens when our documents change?",
      answer:
        "We re-index on change, so an updated policy or SOP is used the moment it's published — no stale answers. Keeping the source documents correct is the one thing that stays with your team.",
    },
    {
      question: "How is it different from a chatbot like ChatGPT?",
      answer:
        "A generic chatbot answers from the public internet and can make things up. This agent answers only from your documents, grounds every claim in a citation, respects who can see what, and abstains when it doesn't know — built for internal accuracy, not chit-chat. It's the internal counterpart to our customer-facing support triage agent.",
    },
  ],
};

const ms: UseCaseContent = {
  industry: "HR, IT & operasi dalaman",
  title: "Ejen pengetahuan dalaman",
  cardSummary:
    "Kakitangan bertanya HR, IT dan operasi sepanjang hari — soalan yang jawapannya sudah ada dalam dokumen anda sendiri.",
  heroSubhead:
    "Kami mereka bentuk, membina dan melaksanakan ejen AI yang menjawab soalan pasukan anda daripada SOP, dasar, buku panduan HR dan dokumen produk anda sendiri — dengan sumber tepat dipetik, dan jujur berkata “itu tiada dalam dokumen kami” apabila memang tiada.",
  snapshot: {
    valueLabel: "Tinggi",
    valueWhy:
      "Membebaskan pakar, HR dan IT anda daripada menjawab soalan yang sama sepanjang hari, dan memberi setiap pekerja jawapan segera, konsisten dan bersumber — nilai merentas seluruh syarikat.",
    complexityLabel: "Sederhana",
    complexityWhy:
      "Perolehan (retrieval) atas dokumen anda, memastikan indeks sentiasa segar, mengasaskan setiap jawapan pada petikan sumber, “saya tidak tahu” yang terkalibrasi, dan akses ikut peranan supaya orang hanya nampak apa yang dibenarkan.",
    timeToLive: "~beberapa minggu (satu set dokumen)",
    bestFit: "Syarikat di mana kakitangan berulang kali bertanya soalan HR/IT/operasi yang sudah ada dalam dokumen",
  },
  outcomes: [
    "Pakar, HR dan IT berhenti menjawab soalan yang sama sepanjang hari",
    "Setiap pekerja dapat jawapan segera dan konsisten — dengan sumbernya",
    "Pekerja baharu cepat menguasai tanpa mengganggu rakan sekerja",
    "Tiada jawapan direka: ia memetik dokumen, atau berkata ia tidak tahu",
  ],
  offTheShelf: {
    title: "Guna siap-sedia dahulu: mungkin anda tak perlu bina tersuai",
    body: "Jujur dengan diri sebelum membina. Jika dokumen anda sudah berada dalam alat yang Claude sambung — Google Drive, SharePoint, Slack, GitHub dan lain-lain — dan anda OK dengan harga setiap kerusi, Claude for Work dengan penyambung (connectors) boleh menyediakan ejen pengetahuan dalaman dengan hampir tiada kejuruteraan: halakan ke sumber anda dan mula. Sebagai rakan Claude, kami cakap terus terang — jika itu sahaja yang anda perlu, mulakan dengan satu pelan. Bina tersuai (halaman ini) berbaloi hanya apabila anda perlukan apa yang produk kerusi tak boleh buat sendiri: akses ikut peranan yang pasukan pematuhan anda boleh buktikan, perolehan diskop ikut jabatan, data terikat-PDPA atau on-prem yang tak boleh berada dalam produk kerusi, permukaan chat anda sendiri seperti WhatsApp atau Teams, atau petikan boleh-audit dan pintu penilaian.",
    plansCta: "Banding pelan Claude",
    costCta: "Anggar kos kerusi",
  },
  impact: {
    manualLabel: "Mencari jawapan hari ini",
    manualValue: "~10–20 min",
    agentLabel: "Dengan ejen",
    agentValue: "~beberapa saat",
    saved:
      "≈ 10–15 minit setiap soalan — tambah kos gangguan tersembunyi pada pakar anda. Pada 500 soalan sebulan, itu ~80–125 jam masa kakitangan dikembalikan.",
    note: "Anggaran; penjimatan sebenar bergantung pada berapa kerap kakitangan mencari jawapan atau mengganggu rakan sekerja sekarang.",
  },
  businessProblem:
    "Di kebanyakan syarikat di rantau ini, jawapan kepada “berapa had tuntutan perubatan kita?”, “bagaimana memohon cuti?”, “borang mana untuk pembekal?” sudah pun wujud — dalam buku panduan HR, SOP, PDF dasar, manual produk. Tetapi tiada siapa boleh menemuinya. Jadi kakitangan menghantar mesej kepada HR, IT atau rakan senior, menunggu balasan, dan soalan yang sama dijawab berpuluh kali seminggu. Pengetahuan itu ada; aksesnya tiada.",
  manualWorkflow: {
    steps: [
      "Pekerja ada soalan",
      "Mencari di pemacu kongsi / intranet, mengalah",
      "Menghantar mesej kepada HR / IT / rakan senior",
      "Menunggu balasan (minit ke jam)",
      "Pakar berhenti kerjanya sendiri untuk menjawab — sekali lagi",
    ],
    breaks:
      "Halangan bukan pengetahuan yang hilang — ia perolehan. Jawapan wujud dalam dokumen yang tiada siapa boleh temui, jadi ia ditanya dan dijawab berulang kali.",
  },
  agenticWorkflow: {
    narrative:
      "Kami menjadikan dokumen anda ejen yang kakitangan boleh terus tanya. Ia mengenal pasti sumber pengetahuan yang berkaitan, memperoleh petikan yang relevan, merangka jawapan berasaskan sumber, dan mengesahkan setiap dakwaan berbalik kepada petikan sebelum menjawab — dan mengaku jujur apabila jawapan tiada dalam dokumen.",
    steps: [
      "Fahami soalan",
      "Perolehi petikan yang relevan",
      "Rangka jawapan berasaskan sumber",
      "Sahkan setiap dakwaan memetik sumber",
      "Jawab dengan jawapan + sumber (atau berkata tidak tahu)",
    ],
    inLabel: "Soalan kakitangan tiba",
    outLabel: "Jawapan bersumber (atau jujur “tiada dalam dokumen”)",
    loopAround: {
      index: 3,
      label: "Jika sesuatu dakwaan tidak disokong sumber diperoleh, ia memperoleh semula atau mengaku tidak tahu — bukan meneka.",
    },
    humanStep: "Soalan yang tak terjawab atau sensitif dihalakan kepada orang yang betul",
  },
  workedExample: {
    inIcon: "whatsapp",
    inLabel: "Mesej kakitangan",
    inMessage:
      "eh nak tanya, panel clinic claim limit berapa ah? and boleh claim non-panel tak?",
    outLabel: "Jawapan bersumber",
    outLines: [
      "Klinik panel: RM 500 setiap lawatan, pesakit luar (Buku Panduan HR §4.2)",
      "Bukan panel: tidak dilindungi melainkan pra-kelulusan pengurus (§4.3)",
      "Tuntut melalui portal HR dalam 30 hari, simpan resit",
    ],
    outResult: "Dijawab dalam beberapa saat, setiap baris dipetik — tiada mesej ke HR",
    note: "Memetik dokumen & seksyen tepat · berkata “tiada dalam dokumen kami” dan bukan meneka · ~RM 0.01–0.05 setiap soalan.",
  },
  workflowDetail: [
    {
      title: "Fahami soalan",
      detail:
        "Ia mentafsir soalan — termasuk campuran Bahasa Malaysia / Inggeris / Cina dan ayat yang kabur — dan mengenal pasti domain pengetahuan yang berkaitan: HR, IT, dasar kewangan, atau manual produk.",
    },
    {
      title: "Perolehi petikan yang betul",
      detail:
        "Ia mencari set dokumen berindeks (semantik serta kata kunci) dan menarik petikan khusus yang menjawab soalan — bukan seluruh dokumen — supaya jawapan berasaskan teks tepat.",
    },
    {
      title: "Rangka jawapan berasaskan sumber",
      detail:
        "Ia menulis jawapan pendek dan langsung menggunakan hanya petikan yang diperoleh, dalam bahasa orang itu bertanya, dan mengekalkan rujukan sumber pada setiap perkara.",
    },
    {
      title: "Sahkan petikan (semakan)",
      detail:
        "Setiap dakwaan dalam jawapan mesti dipetakan kepada sumber diperoleh; jika sesuatu ayat tidak disokong, ia memperoleh semula atau menggugurkannya — supaya ia tidak boleh menyatakan sesuatu yang tiada dalam dokumen anda dengan yakin.",
    },
    {
      title: "Jawab atau mengaku jujur",
      detail:
        "Ia memulangkan jawapan dengan sumbernya, atau berkata “Saya tidak dapat menemuinya dalam dokumen kami” dan menawarkan untuk menghalakan soalan. Tiada tekaan yang yakin.",
    },
    {
      title: "Halakan pengecualian kepada manusia",
      detail:
        "Soalan sensitif, luar skop atau benar-benar tak terjawab dihalakan kepada pemilik yang betul — HR atau IT — dengan soalan disertakan, bukan jalan buntu.",
    },
  ],
  architecture: [
    "Corak: rantaian perolehi → asaskan → semak-petikan atas Claude Agent SDK, dengan bahagian hadapan penghalaan ringan yang memilih set dokumen betul bagi setiap soalan — setiap keupayaan didedahkan sebagai alat MCP.",
    "Pengambilan & perolehan (RAG): dokumen dipecah mengikut strukturnya sendiri — seksyen, klausa, jadual — supaya setiap petikan lengkap dan mengekalkan petikannya, bukan kepingan saiz-tetap membuta yang memotong dasar di tengah klausa. Perolehan adalah hibrid: indeks vektor (embeddings) untuk makna serta indeks kata kunci untuk istilah yang mesti padan tepat — kod dasar, nombor borang, “§4.2”, SKU; kami perolehi lebih dahulu kemudian susun-semula (re-rank) petikan terbaik sebelum model menjawab. Carian semantik semata terlepas pengecam tepat; kata kunci semata terlepas parafrasa — hibrid menangkap kedua-duanya.",
    "Konteks & kesegaran: hanya petikan yang disusun-semula masuk ke tetingkap (prompt-cache untuk konteks sistem yang stabil), dan indeks dibina semula secara berperingkat apabila dokumen berubah — jadi SOP terkini digunakan sebaik diterbitkan, tiada jawapan basi.",
    "Alat: alat carian/perolehan dan pengklasifikasi/penghala topik — semuanya baca-sahaja; tiada alat boleh menulis ke sistem sumber anda.",
    "Pengasasan & petikan: jawapan dihadkan pada petikan diperoleh dan setiap dakwaan membawa sumber; ayat tidak disokong digugurkan — jaminan anti-halusinasi, dikuatkuasakan, bukan diharap.",
    "Kawalan akses: perolehan diskop kepada peranan penanya dan dikuatkuasakan di lapisan alat — dokumen HR atau kewangan terhad hanya boleh dicari oleh orang yang layak, jadi ejen tidak boleh memaparkan apa yang seseorang tidak dibenarkan lihat.",
    "Input tidak dipercayai: kedua-dua soalan dan teks dokumen dianggap tidak dipercayai (tahan suntikan-prompt) — dokumen yang diperangkap tidak boleh membuat ejen membocor atau menyeleweng.",
    "Kebolehcerapan: setiap jawapan mencatat soalan, petikan yang digunakan dan sumber yang dipetik — supaya mana-mana jawapan boleh diaudit, dan dokumen salah atau lapuk dijejak serta dibetulkan.",
    "Rangka penilaian: set emas soalan kakitangan sebenar dengan jawapan betul diketahui serta kes “tiada dalam dokumen”; diskor pada ketepatan jawapan dan pada mengaku bila patut — diuji regresi pada setiap perubahan dan setiap kemas kini dokumen.",
  ],
  models: [
    {
      task: "Klasifikasi & halakan soalan",
      model: "Claude Haiku",
      why: "Klasifikasi niat dan topik yang murah dan pantas pada volum tinggi — kebanyakan soalan tidak perlu lebih.",
    },
    {
      task: "Rangka jawapan berasaskan sumber",
      model: "Claude Sonnet",
      why: "Penaakulan kuat atas petikan diperoleh dan soalan campuran bahasa yang berselerak — kuda kerja.",
    },
    {
      task: "Tafsiran dasar bernuansa / berisiko tinggi",
      model: "Claude Opus",
      why: "Disimpan untuk sebahagian kecil soalan samar dan penting di mana perkataan benar-benar penting.",
    },
  ],
  cost: {
    rows: [
      {
        label: "Penggunaan AI — setiap soalan",
        value: "~RM 0.01–0.05",
        note: "perolehan pendek + jawapan berasaskan sumber, kebanyakannya pada kadar token Claude Haiku/Sonnet",
      },
      {
        label: "Penggunaan AI — pada 500 soalan/bulan",
        value: "~RM 20–100 / bulan",
        note: "kebanyakan soalan selesai pada Haiku; hanya yang bernuansa capai Sonnet atau Opus",
      },
      {
        label: "Pembinaan (sekali)",
        value: "diskop mengikut set dokumen",
        note: "mengindeks dokumen + menyambung permukaan chat adalah beberapa minggu; kami beri sebut harga tetap selepas penemuan ringkas",
      },
      {
        label: "Berterusan",
        value: "pemantauan + indeks-semula",
        note: "kecil berbanding masa pakar yang dibebaskan",
      },
    ],
    disclaimer:
      "Anggaran sahaja, ditunjukkan dalam Ringgit pada kira-kira RM 4.09 kepada USD (kadar pasaran, 11 Ogos 2026); kadar setiap token mengikut harga yang diterbitkan Anthropic (sahkan angka semasa). Penggunaan AI sebenar bergantung pada volum soalan dan berapa banyak teks setiap jawapan perlukan. Anda boleh anggarkan angka yang sama dalam SGD atau mata wang anda dengan kalkulator kos Claude kami.",
  },
  howWeBuild:
    "Ia menjawab hanya daripada dokumen anda sendiri, bukan internet terbuka — jadi ia tidak boleh mereka dasar yang anda tiada. Setiap jawapan membawa sumbernya, dokumen terhad kekal terhad kepada peranan yang betul, dan indeks-semula apabila berubah bermakna SOP terkini terus digunakan sebaik anda terbitkan, bukan berminggu kemudian.",
  agentLoop: [
    "soalan + dokumen berindeks anda",
    "cari & perolehi petikan yang betul (alat MCP)",
    "semak setiap dakwaan memetik sumber sebelum menjawab",
  ],
  integrations: [
    "Simpanan dokumen anda — SharePoint, Google Drive, Confluence, Notion atau PDF",
    "Permukaan chat yang kakitangan sudah guna — Teams, Slack, WhatsApp atau web",
    "Pilihan: HRIS / ITSM anda untuk menghalakan soalan yang ia tidak boleh jawab",
  ],
  modelChoice: {
    approach: "Model tunggal + penghala murah",
    rationale:
      "Menjawab daripada dokumen ialah satu tugas berskop ketat setiap soalan, jadi model tunggal berasaskan sumber dengan pengklasifikasi murah di hadapan adalah pilihan tepat — boleh dipercayai, murah, dan mudah diaudit.",
    costNote:
      "Reka bentuk berbilang-ejen (≈15× token) tidak memberi apa-apa di sini — ini bukan tugas selari, keluasan-dahulu.",
  },
  evaluation: [
    "Semakan jawapan berasaskan sumber — setiap dakwaan mesti dijejak ke sumber diperoleh",
    "Ketepatan mengaku — ia berkata “tiada dalam dokumen” apabila jawapan memang tiada",
    "KPI perniagaan — soalan dilayan sendiri, gangguan pakar dihapuskan, masa-ke-jawapan",
  ],
  evaluationDetail: {
    framework:
      "Kami menilai ejen dengan rangka kerja Agent GPA — Goal, Plan, Action, satu standard semasa untuk kebolehpercayaan ejen — di atas garis dasar manusia.",
    baseline:
      "Mula-mula kami mensampel bagaimana kakitangan dapat jawapan hari ini — berapa lama diambil, berapa kerap pakar diganggu, dan sejauh mana jawapan konsisten. Garis dasar manusia itu menjadi ukuran untuk setiap metrik ejen, jadi peningkatan boleh dibuktikan, bukan diandaikan.",
    dimensions: [
      {
        dim: "Goal",
        desc: "Adakah ia memberi jawapan yang betul?",
        metrics: [
          {
            name: "Ketepatan jawapan",
            measures: "Pada soalan sebenar terpencil, jawapan sepadan dengan jawapan berdokumen yang betul",
            target: "≥ 95%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "Adakah ia menangani soalan dengan kukuh?",
        metrics: [
          {
            name: "Sumber betul diperoleh",
            measures: "Ia menarik petikan yang benar-benar menjawab soalan",
            target: "≥ 95%",
          },
          {
            name: "Mengaku bila patut",
            measures: "Pada soalan yang dokumen tidak liputi, ia berkata demikian dan bukan meneka",
            target: "≥ 98%",
          },
        ],
      },
      {
        dim: "Action",
        desc: "Adakah setiap langkah betul?",
        metrics: [
          {
            name: "Setiap dakwaan dipetik",
            measures: "Tiada ayat dalam jawapan tanpa sumber",
            target: "100%",
          },
          {
            name: "Tiada dasar halusinasi",
            measures: "Ia tidak pernah menyatakan peraturan yang tiada dalam dokumen",
            target: "0 dibenarkan",
          },
          {
            name: "Akses dihormati",
            measures: "Ia tidak pernah memaparkan dokumen yang penanya tidak layak lihat",
            target: "0 dibenarkan",
          },
        ],
      },
    ],
    gate: "Ejen tidak beroperasi kepada kakitangan sehingga ia mengatasi garis dasar manusia dan mencapai sasaran GPA ini pada set emas soalan sebenar — termasuk yang sengaja tiada dalam dokumen. Setiap perubahan dan setiap kemas kini dokumen diuji regresi terhadap set yang sama sebelum dihantar.",
  },
  delivery:
    "Kami bermula dengan bukti-konsep atas satu set dokumen — buku panduan HR atau SOP IT anda — dan membuktikan ia menjawab dengan betul serta mengaku jujur sebelum meluaskan ke lebih banyak sumber. Pelancaran pertama yang fokus mengambil masa beberapa minggu.",
  ctaTitle: "Pasukan anda bertanya HR dan IT soalan yang sama sepanjang hari?",
  ctaDescription:
    "Beritahu kami dokumen mana yang menyimpan jawapan dan bagaimana kakitangan anda bertanya hari ini — kami akan beritahu dengan jujur sama ada ejen pengetahuan dalaman berbaloi dibina, dan apa yang diperlukan.",
  faqs: [
    {
      question: "Dari mana ejen mendapat jawapannya?",
      answer:
        "Hanya daripada dokumen yang anda beri — SOP, dasar, buku panduan dan manual anda — tidak pernah internet terbuka. Jika jawapan tiada dalam dokumen anda, ia berkata demikian dan bukan meneka, dan setiap jawapan memetik sumber tepat.",
    },
    {
      question: "Bagaimana ia sebenarnya mencari jawapan yang betul dalam dokumen kami?",
      answer:
        "Dua langkah. Semasa kami menyediakannya, kami memecah dokumen anda kepada bahagian bermakna — mengikut seksyen dan klausa — supaya ia boleh memetik perenggan tepat, bukan menunjuk kabur ke PDF 40 halaman. Kemudian, bagi setiap soalan, ia mencari dua cara serentak: mengikut makna (jadi “berapa boleh saya tuntut?” menemui klausa “had RM 500” walaupun dalam perkataan berbeza) dan mengikut istilah tepat (jadi “borang BE” atau “klausa 4.2” padan secara literal). Ia mengambil petikan yang paling sepadan, menjawab hanya daripadanya, dan menunjukkan sumbernya. Mencari mengikut makna sahaja akan terlepas kod tepat; mengikut kata kunci sahaja akan terlepas soalan parafrasa yang orang sebenar tanya — melakukan kedua-duanya sebab itu ia mendarat pada petikan yang betul.",
    },
    {
      question: "Bolehkah kakitangan lihat dokumen yang mereka tidak dibenarkan?",
      answer:
        "Tidak. Perolehan diskop kepada peranan setiap orang dan dikuatkuasakan di lapisan alat — dokumen HR atau kewangan terhad hanya boleh dicari oleh orang yang layak. Ejen tidak boleh memaparkan apa yang seseorang tidak dibenarkan lihat.",
    },
    {
      question: "Apa berlaku apabila dokumen kami berubah?",
      answer:
        "Kami indeks-semula apabila berubah, jadi dasar atau SOP terkini digunakan sebaik diterbitkan — tiada jawapan basi. Memastikan dokumen sumber betul ialah satu perkara yang kekal dengan pasukan anda.",
    },
    {
      question: "Apa bezanya dengan chatbot seperti ChatGPT?",
      answer:
        "Chatbot generik menjawab daripada internet awam dan boleh mereka-reka. Ejen ini menjawab hanya daripada dokumen anda, mengasaskan setiap dakwaan pada petikan, menghormati siapa boleh lihat apa, dan mengaku bila tidak tahu — dibina untuk ketepatan dalaman, bukan borak kosong. Ia rakan dalaman kepada ejen triaj sokongan pelanggan kami.",
    },
  ],
};

const zh: UseCaseContent = {
  industry: "HR、IT 与内部运营",
  title: "内部知识智能体",
  cardSummary: "员工整天向 HR、IT 和运营发问——而这些问题的答案早就写在你们自己的文档里。",
  heroSubhead:
    "我们设计、构建并部署一个 AI 智能体，从你们自己的 SOP、政策、HR 手册和产品文档中回答团队的问题——并引用确切来源，遇到文档里没有的内容会诚实地说“这不在我们的文档中”。",
  snapshot: {
    valueLabel: "高",
    valueWhy:
      "把你们的专家、HR 和 IT 从整天重复回答同样的问题中解放出来，并让每位员工都能即时得到一致、有出处的答案——价值遍及整个公司。",
    complexityLabel: "中",
    complexityWhy:
      "对文档的检索、保持索引新鲜、让每个答案都基于引用来源、经过校准的“我不知道”，以及基于角色的访问控制，让人只能看到被允许的内容。",
    timeToLive: "约数周（单一文档集）",
    bestFit: "员工反复询问 HR/IT/运营问题、而答案已在文档中的公司",
  },
  outcomes: [
    "专家、HR 和 IT 不再整天回答同样的问题",
    "每位员工即时得到一致的答案——并附出处",
    "新员工无需打扰同事即可快速上手",
    "不会编造答案：它引用文档，或直接说不知道",
  ],
  offTheShelf: {
    title: "先考虑现成方案：你也许无需定制构建",
    body: "构建前先对自己诚实。如果你的文档已经在 Claude 能连接的工具里——Google Drive、SharePoint、Slack、GitHub 等——而你也能接受按席位付费，那么带连接器的 Claude for Work 几乎无需工程即可搭起一个内部知识智能体：把它指向你的来源即可。作为 Claude 合作伙伴，我们直说——如果这就够了，先从一个方案开始。定制构建（本页其余内容）只有在你需要席位产品自身做不到的能力时才值得：合规团队可证明的基于角色的访问、按部门限定的检索、不能放进席位产品的受 PDPA 约束或本地部署数据、你自己的聊天入口（如 WhatsApp 或 Teams），或可审计的引用与评估门槛。",
    plansCta: "比较 Claude 方案",
    costCta: "估算席位成本",
  },
  impact: {
    manualLabel: "如今找到答案",
    manualValue: "约 10–20 分钟",
    agentLabel: "使用智能体",
    agentValue: "约几秒",
    saved:
      "每个问题约节省 10–15 分钟——外加对专家的隐性打断成本。每月 500 个问题即约 80–125 小时的员工时间被收回。",
    note: "为估算；实际节省取决于员工目前多常自行查找答案或打扰同事。",
  },
  businessProblem:
    "在区域内的大多数公司，“我们的医疗报销限额是多少？”“怎么申请年假？”“对接供应商用哪张表？”这些问题的答案其实早已存在——在 HR 手册、SOP、政策 PDF、产品手册里。但没人找得到。于是员工发消息给 HR、IT 或资深同事，等待回复，同样的问题每周被回答几十次。知识就在那里；缺的是获取的途径。",
  manualWorkflow: {
    steps: [
      "员工有一个问题",
      "在共享盘 / 内网搜索，最后放弃",
      "发消息给 HR / IT / 资深同事",
      "等待回复（几分钟到几小时）",
      "专家停下自己的工作来回答——又一次",
    ],
    breaks:
      "瓶颈不是缺少知识——而是检索。答案存在于一份没人找得到的文档里，于是被反复询问、反复回答。",
  },
  agenticWorkflow: {
    narrative:
      "我们把你们的文档变成一个员工可以直接发问的智能体。它判断问题属于哪个知识来源，检索相关段落，起草一份有据可依的答案，并在回复前核实每一处陈述都能追溯到引用——遇到文档里没有的内容则诚实地表示不知道。",
    steps: [
      "理解问题",
      "检索相关段落",
      "起草有据可依的答案",
      "核实每处陈述都引用来源",
      "附答案 + 出处回复（或说不知道）",
    ],
    inLabel: "一个员工问题进入",
    outLabel: "一份有出处的答案（或诚实的“不在文档中”）",
    loopAround: {
      index: 3,
      label: "若某处陈述没有检索到的来源支撑，它会重新检索或表示不知道，而非臆测。",
    },
    humanStep: "无法回答或敏感的问题被分派给合适的人",
  },
  workedExample: {
    inIcon: "whatsapp",
    inLabel: "员工消息",
    inMessage: "eh nak tanya, panel clinic claim limit berapa ah? and boleh claim non-panel tak?",
    outLabel: "有出处的答案",
    outLines: [
      "指定诊所：每次就诊 RM 500，门诊（HR 手册 §4.2）",
      "非指定诊所：除非经主管预先批准，否则不予报销（§4.3）",
      "30 天内通过 HR 门户报销，保留收据",
    ],
    outResult: "几秒内作答，每一行都有引用——无需发消息给 HR",
    note: "引用确切的文档与章节 · 宁可说“不在我们的文档中”也不臆测 · 每个问题约 RM 0.01–0.05。",
  },
  workflowDetail: [
    {
      title: "理解问题",
      detail:
        "它解读问题——包括马来语 / 英语 / 中文混杂以及含糊的措辞——并判断它属于哪个知识领域：HR、IT、财务政策，还是产品手册。",
    },
    {
      title: "检索正确的段落",
      detail:
        "它在已建索引的文档集中检索（语义加关键词），并只提取回答该问题的具体段落——而非整份文档——使答案基于确切文本。",
    },
    {
      title: "起草有据可依的答案",
      detail:
        "它仅用检索到的段落写出简短、直接的答案，用提问者所用的语言，并为每一点保留来源引用。",
    },
    {
      title: "核实引用（检查环节）",
      detail:
        "答案中的每处陈述都必须对应到一个检索到的来源；若某句无据可依，它会重新检索或删除——因此它无法自信地陈述任何不在你们文档中的内容。",
    },
    {
      title: "作答或诚实弃权",
      detail:
        "它附上来源返回答案，或表示“我在我们的文档中找不到该内容”并提出把问题转交。没有自信的臆测。",
    },
    {
      title: "把例外分派给人",
      detail:
        "敏感、超出范围或确实无法回答的问题会带着问题一并转给合适的负责人——HR 或 IT——而不是走进死胡同。",
    },
  ],
  architecture: [
    "模式：基于 Claude Agent SDK 的 检索 → 落地 → 引用核查 链，配一个轻量的路由前端，为每个问题选择正确的文档集——每项能力都作为 MCP 工具提供。",
    "摄取与检索（RAG）：文档按其自身结构切分——章节、条款、表格——使每个段落自成一体并保留其引用，而非盲目的固定大小切片（会把政策从条款中间切断）。检索是混合式的：用向量（embeddings）索引匹配含义，加上关键词索引匹配必须逐字对上的术语——政策代码、表单编号、“§4.2”、SKU；我们先多召回，再对最佳段落重排序（re-rank），然后模型才作答。纯语义检索会漏掉精确标识符；纯关键词会漏掉改写措辞——混合式两者兼得。",
    "上下文与新鲜度：只有重排后的段落进入窗口（对稳定的系统上下文做提示缓存），并在文档变更时增量重建索引——因此更新后的 SOP 一经发布即刻生效，没有过时答案。",
    "工具：检索/召回工具和一个主题路由/分类器——全部只读；没有任何工具能写入你们的源系统。",
    "落地与引用：答案被限制在检索到的段落内，且每处陈述都带来源；无支撑的句子会被删除——这是强制执行的抗幻觉保证，而非寄望。",
    "访问控制：检索按提问者的角色进行限定，并在工具层强制执行——受限的 HR 或财务文档只有有权限的人才能检索到，因此智能体无法呈现某人无权查看的内容。",
    "不可信输入：问题和文档文本都被视为不可信（可抵御提示注入）——被做手脚的文档无法让智能体泄露信息或行为失常。",
    "可观测性：每个答案都记录问题、所用段落及所引来源——因此任何答案都可审计，错误或过时的文档也能被追溯并修正。",
    "评估框架：一组真实员工问题的黄金集，含已知正确答案以及已知的“不在文档中”案例；同时对答案正确性和该弃权时是否弃权进行评分——每次改动和每次文档更新都做回归测试。",
  ],
  models: [
    {
      task: "对问题分类与路由",
      model: "Claude Haiku",
      why: "对高频问题做便宜、快速的意图与主题分类——大多数问题无需更多。",
    },
    {
      task: "起草有据可依的答案",
      model: "Claude Sonnet",
      why: "对检索段落和杂乱混合语言问题具备强推理能力——主力模型。",
    },
    {
      task: "微妙 / 高风险的政策解读",
      model: "Claude Opus",
      why: "留给一小部分含糊且后果重大、措辞至关重要的问题。",
    },
  ],
  cost: {
    rows: [
      {
        label: "AI 用量——每个问题",
        value: "约 RM 0.01–0.05",
        note: "简短检索 + 有据答案，主要按 Claude Haiku/Sonnet 的 token 费率",
      },
      {
        label: "AI 用量——每月 500 个问题",
        value: "约 RM 20–100 / 月",
        note: "大多数问题在 Haiku 上解决；只有微妙的才触及 Sonnet 或 Opus",
      },
      {
        label: "构建（一次性）",
        value: "按文档集范围报价",
        note: "为你的文档建索引 + 接入聊天入口约需数周；简短的需求梳理后我们给出固定报价",
      },
      {
        label: "持续运营",
        value: "监控 + 重新建索引",
        note: "相比其释放的专家时间，成本很小",
      },
    ],
    disclaimer:
      "仅为估算，按约 1 美元兑 4.09 令吉（市场中间价，2026 年 8 月 11 日）以令吉显示；每 token 费率以 Anthropic 公布的定价为准（请核对最新数字）。实际 AI 用量取决于问题量以及每个答案所需的文本量。你可以用我们的 Claude 成本计算器把同样的数字换算成 SGD 或你的货币。",
  },
  howWeBuild:
    "它只从你们自己的文档作答，而非公开互联网——因此它无法编造一条你们并不存在的政策。每个答案都带来源，受限文档始终只对正确的角色开放，而变更时重新建索引意味着更新后的 SOP 一经发布即刻生效，而非数周之后。",
  agentLoop: ["问题 + 你们已建索引的文档", "检索并提取正确段落（MCP 工具）", "回复前核查每处陈述都引用来源"],
  integrations: [
    "你们的文档库——SharePoint、Google Drive、Confluence、Notion 或 PDF",
    "员工已在用的聊天入口——Teams、Slack、WhatsApp 或网页",
    "可选：你们的 HRIS / ITSM，用于分派它无法回答的问题",
  ],
  modelChoice: {
    approach: "单一模型 + 一个便宜的路由器",
    rationale:
      "从文档作答对每个问题都是一项范围严格的任务，因此一个有据可依的单一模型配上前置的便宜分类器是正确选择——可靠、省成本、也易审计。",
    costNote:
      "多智能体设计（约 15 倍 token）在这里毫无收益——这不是一个并行、广度优先的任务。",
  },
  evaluation: [
    "有据答案检查——答案中每处陈述都必须追溯到检索到的来源",
    "弃权准确度——当答案确实不存在时，它会说“不在文档中”",
    "业务 KPI——自助解决的问题数、消除的专家打断、作答用时",
  ],
  evaluationDetail: {
    framework:
      "我们采用 Agent GPA 框架——Goal、Plan、Action，当前衡量智能体可靠性的标准——并结合人工基线来评估。",
    baseline:
      "我们先取样员工如今如何获得答案——耗时多久、专家被打断多频繁、答案有多一致。这个人工基线是衡量每个智能体指标的基准，因此提升是可证明的，而非假设。",
    dimensions: [
      {
        dim: "Goal",
        desc: "它给出的答案对吗？",
        metrics: [
          {
            name: "答案正确率",
            measures: "在留出的真实问题上，答案与文档中的正确答案一致",
            target: "≥ 95%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "它处理问题的方法是否稳妥？",
        metrics: [
          {
            name: "检索到正确来源",
            measures: "它提取的是真正回答问题的那段",
            target: "≥ 95%",
          },
          {
            name: "该弃权时弃权",
            measures: "对文档未涵盖的问题，它会说明而非臆测",
            target: "≥ 98%",
          },
        ],
      },
      {
        dim: "Action",
        desc: "各个步骤是否正确？",
        metrics: [
          {
            name: "每处陈述都有引用",
            measures: "答案中没有任何一句缺少来源",
            target: "100%",
          },
          {
            name: "无幻觉政策",
            measures: "它绝不陈述文档中不存在的规则",
            target: "零容忍",
          },
          {
            name: "尊重访问权限",
            measures: "它绝不呈现提问者无权查看的文档",
            target: "零容忍",
          },
        ],
      },
    ],
    gate: "在智能体于真实问题黄金集（含故意不在文档中的问题）上超过人工基线并达到这些 GPA 目标之前，它不会面向员工上线。每次改动和每次文档更新在上线前都要针对同一数据集做回归测试。",
  },
  delivery:
    "我们先在一个文档集上做概念验证——你的 HR 手册或 IT SOP——先证明它作答正确且能诚实弃权，再扩展到更多来源。聚焦的首次落地只需数周。",
  ctaTitle: "你的团队整天在向 HR 和 IT 问同样的问题吗？",
  ctaDescription:
    "告诉我们哪些文档藏着答案、员工如今如何发问——我们会诚实告诉你内部知识智能体是否值得构建，以及需要什么。",
  faqs: [
    {
      question: "智能体的答案从哪里来？",
      answer:
        "只来自你提供的文档——你的 SOP、政策、手册和说明书——绝不来自公开互联网。如果答案不在你的文档中，它会如实说明而非臆测，且每个答案都引用确切来源。",
    },
    {
      question: "它究竟如何在我们的文档中找到正确答案？",
      answer:
        "两步。在部署时，我们把你们的文档切分成有意义的片段——按章节和条款——这样它能引用确切的段落，而不是含糊地指向一份 40 页的 PDF。然后，对每个问题，它同时用两种方式检索：按含义（这样“我能报销多少？”即使措辞不同也能找到“RM 500 限额”那条）和按精确术语（这样“form BE”或“条款 4.2”能逐字匹配）。它取出最匹配的段落，只依据这些作答，并展示出处。只按含义检索会漏掉精确代码；只按关键词会漏掉人们实际会问的改写措辞——两者都做，正是它能命中正确段落的原因。",
    },
    {
      question: "员工会看到无权查看的文档吗？",
      answer:
        "不会。检索按每个人的角色进行限定，并在工具层强制执行——受限的 HR 或财务文档只有有权限的人才能检索到。智能体无法呈现某人无权查看的内容。",
    },
    {
      question: "我们的文档更新后会怎样？",
      answer:
        "我们在变更时重新建索引，因此更新后的政策或 SOP 一经发布即被采用——不会有过时答案。保持源文档正确，是唯一留在你团队手中的事。",
    },
    {
      question: "它和 ChatGPT 这类聊天机器人有何不同？",
      answer:
        "通用聊天机器人从公开互联网作答，可能编造内容。这个智能体只从你的文档作答、让每处陈述都基于引用、尊重谁能看到什么、并在不知道时弃权——为内部准确性而建，而非闲聊。它是我们面向客户的工单分流智能体的内部对应版本。",
    },
  ],
};

export const knowledgeAgent: AgentUseCase = {
  structure: {
    slug: "knowledge-agent",
    status: "available",
    pattern: "prompt-chaining",
    route: "solutions/agent-use-cases/knowledge-agent",
    heroImage: "/images/agent-use-cases/knowledge-agent-hero.jpg",
    cardImage: "/images/agent-use-cases/knowledge-agent-hero.jpg",
    relatedUseCases: ["support-triage", "product-enquiry"],
    trackLocation: "agent_use_case_knowledge_agent",
    valueScore: 4,
    complexityScore: 3,
  },
  content: { en, ms, zh },
};
